import { Dispatch, SetStateAction, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { StatsigClient, StatsigProvider, useClientAsyncInit } from "@statsig/react-bindings";
//import { StatsigProvider } from "statsig-react";

import showdown from "showdown";
import styled from "styled-components";
import { Model, Serializer } from "survey-core";
import {
  AfterRenderPageEvent,
  CompletingEvent,
  CurrentPageChangedEvent,
  TextMarkdownEvent,
} from "survey-events-api";
import { Survey } from "survey-react-ui";

import { BlackFridayTimer } from "./features/BlackFridayTimer";
import ProgressHeader from "./features/ProgressHeader";
import { LandingPage } from "./LandingPage";
import {
  COMPONENT_CONFIG,
  findPageIndex,
  getQuestionHeading,
  getQuestionSection,
  getSectionInfo,
  isTransitionComponentKey,
  QuizSection,
  SURVEY_JSON,
} from "./quiz";
import { Goals } from "./quiz/goals";
import {
  FunnelVariation,
  pushToDataLayer,
  QuizEventData,
  sendChangesToGTM,
  sendPageViewEvent,
} from "./services/googleTagManager";
import { submitUserToHubspot } from "./services/hubspot";
import { User } from "./types";

import * as amplitude from "@amplitude/analytics-browser";
import Cookies from "js-cookie";
import { Route, Routes, useLocation, useNavigate } from "react-router-dom";
import './App.scss';
import { getCdnImageUrl } from "./features/images";
import {
  getProgramNameForUser,
  LANDING_PAGE_CHOICES,
} from "./features/programs/Program.config";
import { getVersionNumber, STATSIG_CLIENT_KEY } from "./services/experiments";
// @ts-ignore
import { Identify } from '@amplitude/analytics-browser';

import { CheckoutPage } from './CheckoutPage';

import { v4 as uuidv4 } from "uuid";
import QuestionFooter from "./features/QuestionFooter";
import QuestionHeader from "./features/QuestionHeader";
import BridgeScreen from "./features/BridgeScreen";
import { BRIDGE_CONTENT } from "./features/bridgeContent";

// Import custom carousel question type
import axios from "axios";
import { DevTool } from "./features/DevTool";
import { useAnswerTracker } from "./hooks/useAnswerTracker";
import "./quiz/carousel";
import { getAssignedExperimentArm, getCookie, getUTMParams } from "./utils";
import { trackCustom } from "./services/facebookPixel";
import { trackQuizCompleted, trackQuizStarted } from "./services/googleTagManager";
import { Project60StickyBanner } from './Project60StickyBanner';

let vh = window.innerHeight * 0.01;
document.documentElement.style.setProperty('--vh', `${vh}px`);
window.addEventListener('resize', () => {
  // We execute the same script as before
  let vh = window.innerHeight * 0.01;
  document.documentElement.style.setProperty('--vh', `${vh}px`);
});

const AMPLITUDE_API_KEY = process.env.REACT_APP_AMPLITUDE_API_KEY || '';

// sorted somewhat in the order we need them
const IMAGE_PATHS = [
  'images/bodyfat/female35.jpg',
  'images/bodyfat/female20.jpg',
  'images/bodyfat/male40.jpg',
  'images/bodyfat/female30.jpg',
  'images/bodyfat/female18.jpg',
  'images/bodyfat/female25.jpg',
  'images/bodyfat/goalMale10.jpg',
  'images/bodyfat/male20.jpg',
  'images/bodyfat/male35.jpg',
  'images/bodyfat/female40.jpg',
  'images/bodyfat/goalMale12.jpg',
  'images/bodyfat/female45.jpg',
  'images/bodyfat/goalMale15.jpg',
  'images/bodyfat/male25.jpg',
  'images/bodyfat/male30.jpg',
  'images/bodyfat/male15.jpg',
  'images/bodyfat/male12.jpg',
  'images/bodyfat/goalMale20.jpg',
  'images/bodyfat/male10.jpg',
  'images/bodyfat/female15.jpg',
  'images/bodytype/Male/overweight.jpg',
  'images/bodytype/Male/skinny.png',
  'images/bodytype/Male/bulky.jpg',
  'images/bodytype/Male/skinnyfat.png',
  'images/bodytype/Female/overweight.jpg',
  'images/bodytype/Female/skinny.jpg',
  'images/bodytype/Female/bulky.png',
  'images/bodytype/Female/skinnyfat.jpg',
  'images/bws-loading-chart-2.jpg',
  'images/ktla.webp',
  'images/womensFitness.jpg',
  'images/splash/timeMale.jpg',
  'images/splash/protein.jpg',
  'images/splash/goodHands.jpg',
  'images/splash/timeFemale.jpg',
  'images/splash/habit.jpg',
  'images/splash/injuriesFemale.jpg',
  'images/splash/injuriesMale.jpg',
  'images/splash/busyLevel.jpg',
  'images/testimonials/brian.jpg',
  'images/testimonials/brian.png',
  'images/testimonials/jade.png',
  'images/testimonials/jade.jpg',
  'images/testimonials/cesar.png',
  'images/testimonials/cesar.jpg',
  'images/testimonials/katy.jpg',
  'images/testimonials/stephen.jpg',
  'images/testimonials/jesse.jpg',
  'images/testimonials/patrick.jpg',
  'images/testimonials/abdullah.jpg',
  'images/testimonials/salma.jpg',
  'images/testimonials/salma.png',
  'images/testimonials/ligia.jpg',
  'images/testimonials/adam.png',
  'images/testimonials/adam.jpg',
  'images/testimonials/reinis.jpg',
  'images/testimonials/tahnee.jpg',
  'images/testimonials/jan.jpg',
  'images/testimonials/jan.png',
  'images/youtube_social_icon_white.png',
  'images/TikTok_Icon_Black_Circle.png',
  'images/yahoo.jpg',
  'images/landingPage/FemaleStrongPremium.jpg',
  'images/landingPage/MaleBuildPremium.jpg',
  'images/landingPage/FemaleStrongBase.jpg',
  'images/landingPage/FemaleLeanElite.jpg',
  'images/landingPage/MaleBuildElite.jpg',
  'images/landingPage/MaleShredElite.jpg',
  'images/landingPage/FemaleLeanBase.jpg',
  'images/landingPage/MaleBuildBase.jpg',
  'images/landingPage/MaleShredPremium.jpg',
  'images/landingPage/FemaleLeanPremium.jpg',
  'images/landingPage/MaleShredBase.jpg',
  'images/landingPage/FemaleStrongElite.jpg',
  'images/f_logo_RGB-Blue_144.png',
  'images/mensHealth.jpg',
  'images/bws-loading-chart.jpg',
  'images/bws-images-basic/basic-build-intermediate.png',
  'images/bws-images-basic/basic-build.png',
  'images/bws-images-basic/basic-lean-intermediate.png',
  'images/bws-images-basic/basic-lean.png',
  'images/bws-images-basic/basic-shred-intermediate.png',
  'images/bws-images-basic/basic-shred.png',
  'images/bws-images-basic/basic-strong-intermediate.png',
  'images/bws-images-basic/basic-strong.png',
  'images/bws-images-basic/BWS%2BElite+Female.jpg',
  'images/bws-images-basic/BWS%2BElite+Male.jpg',
  'images/bws-images-basic/BWS%2BPremium+Female.jpg',
  'images/bws-images-basic/BWS%2BPremium+Male.jpg',
  'images/new-quiz-bodytype-images-2025/male-bodytype/1.png',
  'images/new-quiz-bodytype-images-2025/male-bodytype/2.png',
  'images/new-quiz-bodytype-images-2025/male-bodytype/3.png',
  'images/new-quiz-bodytype-images-2025/male-bodytype/4.png',
  'images/new-quiz-bodytype-images-2025/male-bodytype/5.png',
  'images/new-quiz-bodytype-images-2025/male-bodytype/6.png',
  'images/new-quiz-bodytype-images-2025/female-bodytype/1.png',
  'images/new-quiz-bodytype-images-2025/female-bodytype/2.png',
  'images/new-quiz-bodytype-images-2025/female-bodytype/3.png',
  'images/new-quiz-bodytype-images-2025/female-bodytype/4.png',
  'images/new-quiz-bodytype-images-2025/female-bodytype/5.png',
  'images/new-quiz-bodytype-images-2025/female-bodytype/6.png',


  'images/new-quiz-bodyfat-images/Male/10.png',
  'images/new-quiz-bodyfat-images/Male/12.png',
  'images/new-quiz-bodyfat-images/Male/15.png',
  'images/new-quiz-bodyfat-images/Male/18.png',
  'images/new-quiz-bodyfat-images/Male/20.png',
  'images/new-quiz-bodyfat-images/Male/40.png',
  'images/new-quiz-bodyfat-images/Male/22.png',
  'images/new-quiz-bodyfat-images/Male/25.png',
  'images/new-quiz-bodyfat-images/Male/28.png',
  'images/new-quiz-bodyfat-images/Male/30.png',
  'images/new-quiz-bodyfat-images/Male/32.png',
  'images/new-quiz-bodyfat-images/Male/35.png',
  'images/new-quiz-bodyfat-images/Female/18.png',
  'images/new-quiz-bodyfat-images/Female/20.png',
  'images/new-quiz-bodyfat-images/Female/22.png',
  'images/new-quiz-bodyfat-images/Female/25.png',
  'images/new-quiz-bodyfat-images/Female/28.png',
  'images/new-quiz-bodyfat-images/Female/30.png',
  'images/new-quiz-bodyfat-images/Female/32.png',
  'images/new-quiz-bodyfat-images/Female/35.png',
  'images/new-quiz-bodyfat-images/Female/38.png',
  'images/new-quiz-bodyfat-images/Female/40.png',
  'images/new-quiz-bodyfat-images/Female/42.png',
  'images/new-quiz-bodyfat-images/Female/45.png',
  'images/new-quiz-bodyfat-images/Female/50.png',
  'images/new-quiz-bodyfat-images/Female/15.png',
];

const AppContainer = styled.div`
  font-family: 'Lato', sans-serif;

  min-height: 100vh;
  min-height: calc(var(--vh, 1vh) * 100);

  display: flex;
  flex-direction: column;
  &:has(.quote-container) {
    background-color: #add8e6;
  }
  &:has(.new-quote-container) {
    background: linear-gradient(180deg, #E7F0FF 10.59%, #FFFFFF 86.15%);
    .sd-root-modern {
      background: transparent;
      display: flex;
      justify-content: center;
    }
  }

  &:has(.new-quote-container.centered) {
    .sd-root-modern {
      align-items: center;
    }
  } 
`;

const HeaderContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  text-align: center;
`;

export const LOCAL_STORAGE_KEY = 'BWS_SURVEY_DATA';
const LAST_UPDATED_KEY = 'BWS_LAST_UPDATED';
const SEVEN_DAY_IN_MS = 7 * 24 * 60 * 60 * 1000;

const useImagePreloader = () => {
  useEffect(() => {
    const preloadImages = () => {
      const promises = IMAGE_PATHS.map((path) => {
        return new Promise((resolve, reject) => {
          const img = new Image();
          img.onload = resolve;
          img.onerror = reject;
          img.src = getCdnImageUrl(path);
        });
      });

      Promise.all(promises).catch((error) => {
        console.error('Error preloading images:', error);
      });
    };
    preloadImages();
  }, []);
};

type TransitionComponentProps<T> = {
  user: T;
  survey: Model;
  onComplete: () => void;
};

Serializer.addProperty("page", {
  name: "props",
  category: "general",
  default: {},
});

const markdownToHtmlFuncGenerator = (converter: showdown.Converter) => (_: Model, options: TextMarkdownEvent) => {
  const { element, name, text } = options;
  const isSurveyPartToUpdate = ['title', 'description'].includes(name);
  const isSurveyType = element.getType() !== 'survey';
  const allowedHtmlQuestionNames = ['goals', 'describeSelf', 'experience', 'science', 'cardio', 'comfort', 'equipment', 'typeofsupport', 'activity', 'mainNutritionHelp', 'foodTracking', 'workoutPlan', 'concerns'];
  const isHtmlAllowed = allowedHtmlQuestionNames.includes(element.name);
  const isTextName = name === 'text';

  if ((isSurveyPartToUpdate && isSurveyType) || (isHtmlAllowed && isTextName)) {
    const htmlStr = converter.makeHtml(text);
    const startTag = '<p>';
    const endTag = '</p>';
    const startTagIndex = htmlStr.indexOf(startTag);
    const endTagIndex = htmlStr.lastIndexOf(endTag);

    options.html = startTagIndex !== -1 && endTagIndex !== -1 ? htmlStr.slice(startTagIndex + startTag.length, endTagIndex) : htmlStr;
  }
};

const onAfterRenderPageFuncGenerator =
  (setProgressPercent: Dispatch<SetStateAction<number>>, setPageTotal: Dispatch<SetStateAction<number>>, setTitle: Dispatch<SetStateAction<QuizSection>>) =>
  (_: Model, options: AfterRenderPageEvent) => {
    const questionName = options.page.name;
    const section = getQuestionSection(questionName);
    if (!!section) {
      const sectionInfo = getSectionInfo(section);
      setTitle(section);

      // Calculate total questions across all sections
      const totalQuestions = Object.values(QuizSection).reduce((sum, sectionKey) => {
        const sectionTotal = getSectionInfo(sectionKey as QuizSection);
        return sum + sectionTotal.total;
      }, 0);
      setPageTotal(totalQuestions);

      // Calculate progress based on section
      let progress = 0;
      if (section === QuizSection.GETTING_STARTED) {
        // First section is 50% of total progress
        progress = Math.floor((findPageIndex(questionName) - sectionInfo.startIndex + 1) * (50 / sectionInfo.total));
      } else if (section === QuizSection.HABITS_AND_BEHAVIORS) {
        // Second section is 25% of total progress
        progress = 50 + Math.floor((findPageIndex(questionName) - sectionInfo.startIndex + 1) * (25 / sectionInfo.total));
      } else if (section === QuizSection.TRAINING_AND_NUTRITION) {
        // Third section is 25% of total progress
        progress = 75 + Math.floor((findPageIndex(questionName) - sectionInfo.startIndex + 1) * (25 / sectionInfo.total));
      }
      setProgressPercent(progress);

    } else {
      if (process.env.NODE_ENV === 'development') {
        console.error(`Missing section configuration for question: ${questionName}`); // TODO send errors to some logging service
      }
    }
  };

const onCurrentPageChangedFuncGenerator =
  (config: QuizTransitionConfig, setTransitionComponentKey: Dispatch<SetStateAction<string | null>>, setUser: Dispatch<SetStateAction<User>>, trackAnswer: (questionName: string, answer: any, allAnswers: any) => void) =>
  (sender: Model, options: CurrentPageChangedEvent) => {
    const { oldCurrentPage, newCurrentPage } = options;
    const transitionConfig = config[oldCurrentPage.name];
    if (transitionConfig && transitionConfig.nextPage === newCurrentPage.name) {
      setTransitionComponentKey(oldCurrentPage.name);
    }
    sendChangesToGTM(sender, oldCurrentPage, findPageIndex(oldCurrentPage.name) + 1);

    const previousPageName = oldCurrentPage.name;
    const answer = sender.data[previousPageName];
    //trackAnswer(previousPageName, answer, sender.data);

    if (oldCurrentPage.name === 'email') {
      // we only want to submit the user once they have entered their email

      submitUserToHubspot(sender.data, false, "no_trial").then(userId =>{
        const identifyObjUser = new Identify();
      identifyObjUser.set('HubspotUserID',userId);
      amplitude.identify(identifyObjUser);
      });


    }
    setUser((user) => ({
      ...user,
      [oldCurrentPage.name]: sender.data[oldCurrentPage.name],
      ...(oldCurrentPage.name === 'weight'
        ? {
            weightUnit: sender.data.weightUnit,
          }
        : {}),
      ...(oldCurrentPage.name === 'height'
        ? {
            heightUnit: sender.data.heightUnit,
            heightRaw: sender.data.heightRaw,
            heightInches: sender.data.heightInches,
          }
        : {}),
    }));


  };

const onCompletingFuncGenerator =
  (setTransitionComponentKey: Dispatch<SetStateAction<string | null>>, setUser: Dispatch<SetStateAction<User>>) => (sender: Model, _: CompletingEvent) => {
    const pageName: string = sender.currentPage.name;

    setTransitionComponentKey(pageName);

    sendChangesToGTM(sender, sender.currentPage, findPageIndex(sender.currentPage.name) + 1);
    setUser((user) => ({
      ...user,
      [pageName]: sender.data[pageName],
    }));
    if (isTransitionComponentKey(pageName)) {
      const goal = sender.data.goals as Goals;
      sendPageViewEvent(
        goal,
        COMPONENT_CONFIG[pageName].componentName,
        getProgramNameForUser(sender.data),
        LANDING_PAGE_CHOICES,
        findPageIndex(COMPONENT_CONFIG[pageName].componentName) + 1
      );
    }
    if (isTransitionComponentKey(pageName)) {
      submitUserToHubspot(sender.data, true, "no_trial").then(userId =>{
        const identifyObjUser = new Identify();
        identifyObjUser.set('HubspotUserID',userId);
        amplitude.identify(identifyObjUser);
      });
    }
  };

type QuizTransitionConfig = {
  [key: string]: {
    nextPage?: string;
    component?: (props: TransitionComponentProps<User>) => JSX.Element;
  };
};

let didAddCustomAutoAdvanceHandler = false;

function useSurvey(
  surveyJson: Object,
  converter: showdown.Converter,
  statsigClient: StatsigClient,
  isStatsigClientLoading: boolean
): [
  Model,
  number,
  ((props: TransitionComponentProps<User>) => JSX.Element) | undefined,
  string | undefined,
  Dispatch<SetStateAction<string | null>>,
  QuizSection,
  User
] {
  const [pageNo, setPageNo] = useState(0);
  const [pageTotal, setPageTotal] = useState(1);
  const [progressPercent, setProgressPercent] = useState(0);
  const [section, setSection] = useState<QuizSection>(
    QuizSection.GETTING_STARTED
  );
  const [user, setUser] = useState<User>({} as User);
  const [transitionComponentKey, setTransitionComponentKey] = useState<
    string | null
  >(null);
  const transitionComponent = useMemo(() => {
    if (isTransitionComponentKey(transitionComponentKey)) {
      return COMPONENT_CONFIG[transitionComponentKey].component;
    }
  }, [transitionComponentKey]);
  const transitionComponentName = useMemo(() => {
    if (isTransitionComponentKey(transitionComponentKey)) {
      return COMPONENT_CONFIG[transitionComponentKey].componentName;
    }
  }, [transitionComponentKey]);

  const navigate = useNavigate();
  const { trackAnswer } = useAnswerTracker();

  const survey = useMemo(() => new Model(surveyJson), [surveyJson]);

  useEffect(() => {
    const referralPageNames = [
      "trainingcircle",
      "invitationcomfort",
      "whomotivatesyou",
      "invitationcomfortfollow",
      "fasterprogresstogether",
      "guestPassInvites",
    ];
    for (const name of referralPageNames) {
      const page = survey.getPageByName?.(name);
      if (page) page.visible = false;
    }
  }, [survey]);

  survey.onGetQuestionTitleActions.add((_, options) => {
    (options as any).actions = [];
  });

  survey.onUpdateQuestionCssClasses.add(function (survey, options) {
    const leftAlignQuestionNames = ["goals"];
    if (
      leftAlignQuestionNames.includes(options.question.name) &&
      !options.cssClasses.root.includes("left-align")
    ) {
      options.cssClasses.root += " left-align";
    }
  });

  const onAfterRenderPageHandler = useMemo(
    () =>
      onAfterRenderPageFuncGenerator(
        setProgressPercent,
        setPageTotal,
        setSection
      ),
    [setProgressPercent, setPageTotal, setSection]
  );
  const markdownToHtmlConverter = useMemo(
    () => markdownToHtmlFuncGenerator(converter),
    [converter]
  );
  const onCurrentPageChangedHandler = useMemo(
    () =>
      onCurrentPageChangedFuncGenerator(
        COMPONENT_CONFIG,
        setTransitionComponentKey,
        setUser,
        trackAnswer
      ),
    [setTransitionComponentKey, setUser, trackAnswer]
  );
  const onCompletingHandler = useMemo(
    () => onCompletingFuncGenerator(setTransitionComponentKey, setUser),
    [setTransitionComponentKey, setUser]
  );
  const onCompleteHandler = () => {
    // Fire tracking events for quiz completion
    trackCustom("QuizFinished");
    trackQuizCompleted();

    if (window.location.pathname !== "/checkout") {
      navigate("/results");
    }
  };

  const onCurrentPageChangingHandler = useCallback(
    (sender: any, options: any) => {
      if (!options.isNextPage) {
        return;
      }
      const question = options.oldCurrentPage.name;
      const answer = sender.data[question];
      trackAnswer(question, answer, sender.data);
    },
    []
  );

  useEffect(() => {
    const lastUpdated = localStorage.getItem(LAST_UPDATED_KEY);
    const currentTime = Date.now();
    if (lastUpdated) {
      if (currentTime - parseInt(lastUpdated, 10) > SEVEN_DAY_IN_MS) {
        // Clear localStorage and reset form
        localStorage.removeItem(LOCAL_STORAGE_KEY);
        localStorage.removeItem(LAST_UPDATED_KEY);
        survey.currentPageNo = 0;
      }
    }

    const storedSurvey = localStorage.getItem(LOCAL_STORAGE_KEY);
    const { currentPageIndex = 0 } = storedSurvey
      ? JSON.parse(storedSurvey)
      : {};

    if (currentPageIndex && storedSurvey) {
      const { data } = JSON.parse(storedSurvey);
      Object.entries(data).forEach(([key, value]) => {
        survey.setValue(key, value);
      });
      // Navigate to the saved page
      survey.currentPageNo = currentPageIndex;
      // setUser((prevUser) => ({ ...prevUser, ...data }));
      // Ensure values invalid for the current experience are cleared
      const experienceValue = survey.data?.experience;
      if (experienceValue === "Beginner") {
        const invalidNumberWorkouts = ["As many as possible"];
        if (invalidNumberWorkouts.includes(survey.data?.numberWorkouts)) {
          survey.setValue("numberWorkouts", undefined);
        }
        if (survey.data?.timeAvailable === "30OrLess") {
          survey.setValue("timeAvailable", undefined);
        }
      }
    }

    const saveProgress = (a: any, b: any) => {
      const currentData = {
        currentPageIndex: survey.currentPageNo,
        data: survey.data,
      };
      // Some keys in survey data can become undefined during the survey flow,
      // so we spread the user object here to ensure we don't lose any data
      localStorage.setItem(
        LOCAL_STORAGE_KEY,
        JSON.stringify({
          ...currentData,
          data: { ...user, ...currentData.data },
        })
      );
      localStorage.setItem(LAST_UPDATED_KEY, Date.now().toString());
    };

    const customGuestPassSkipNavigation = (sender: any, options: any) => {
      const page = sender.currentPage;
      if (!page) return;

      const existing = document.querySelector("#skipButton");
      if (existing) existing.remove();

      if(page.name !== "guestPassInvites") {
        return;
      }

      // find nav bar
      const nav = Array.from(document.querySelectorAll("span"))
        .find(el => el.textContent?.trim() === "Invite");
      if (!nav || !nav.parentNode || !nav.parentNode.parentNode) return;

      const parentNode = nav.parentNode.parentNode;

      // only show Skip on page that has the guestPassInvites question
      const guestPassQ = sender.getQuestionByName("guestPassInvites");
      if (!guestPassQ || !page.containsElement(guestPassQ)) return;

      // create Skip button
      const btn = document.createElement("button");
      btn.id = "skipButton";
      btn.type = "button";
      btn.textContent = "Skip";

      // style so it shows under Continue
      btn.style.marginTop = "8px";
      btn.style.textAlign = "center";
      btn.style.backgroundColor = "white";
      btn.style.border = "0";
      btn.style.cursor = "pointer";
      btn.style.fontSize = "16px"
      btn.style.color = "#4A4A4A";
      btn.style.fontStyle = "normal";
      btn.style.lineHeight = "1.5";
      btn.style.fontWeight = "400";
      btn.style.marginTop = "16px";

      // click handler: go to page with followingus
      btn.addEventListener("click", () => {
        const followingQ = sender.getQuestionByName("followingus");
        if (followingQ) {
          sender.currentPage = sender.getPageByQuestion(followingQ);
        }
      });

      // append to nav bar
      parentNode.appendChild(btn);
    }

    const project60OptOut = (sender: any, options: any) => {
      const page = sender.currentPage;

      const existing = document.querySelector("#optOutButton");
      if (existing) existing.remove();

      if(page.name !== "project60") {
        return;
      }
      const btn = document.createElement("button");
      btn.id = "optOutButton";
      btn.type = "button";
      btn.textContent = "Opt out";
      btn.className = "project60-opt-out-button";

      // style so it shows under Continue
      btn.style.marginTop = "8px";
      btn.style.textAlign = "center";
      btn.style.backgroundColor = "white";
      btn.style.border = "0";
      btn.style.cursor = "pointer";
      btn.style.fontSize = "16px"
      btn.style.color = "#4A4A4A";
      btn.style.fontStyle = "normal";
      btn.style.lineHeight = "1.5";
      btn.style.fontWeight = "400";
      btn.style.marginTop = "16px";

      // click handler: go to page with followingus
      btn.addEventListener("click", () => {
        console.log("sender", sender);
        sender.currentPage.questions[0].value = false;
        sender.nextPage();
      });

      const container = document.getElementById("question-footer-container");
      if (container?.querySelectorAll("#optOutButton")?.length === 0) {
        document.getElementById("question-footer-container")?.appendChild(btn);
      }
    }

    // Custom auto-advance logic based on page configuration
    const customAutoAdvanceHandler =  (sender: any) => {
      const currentPage = sender.currentPage;
      if (!currentPage) return;

      // Find the page configuration
      const pageConfig = SURVEY_JSON.pages.find(
        (p: any) => p.name === currentPage.name
      ) as any;

      // Skip auto-advance if page has noAutoAdvance property set to true
      if (pageConfig?.noAutoAdvance) {
        return;
      }

      // Check if all questions on the current page are answered
      const questions = currentPage.questions;
      for (let i = 0; i < questions.length; i++) {
        if (questions[i].hasInput && questions[i].isEmpty()) return;
      }

      // Check for errors
      if (currentPage.hasErrors(false)) return;

      // Auto-advance to next page with a small delay to prevent ghost taps
      if (!sender.isLastPage) {
        // Clear any active element focus/highlight
        const activeElement = document.activeElement as HTMLElement;
        if (activeElement && typeof activeElement.blur === "function") {
          activeElement.blur();
        }

        // Add transitioning class to prevent interactions during page change
        const surveyRoot = document.querySelector(".sd-root-modern");
        if (surveyRoot) {
          surveyRoot.classList.add("transitioning");
        }

        // Add a small delay before navigating to prevent ghost taps on mobile
        setTimeout(() => {
          sender.nextPage();
          // Remove transitioning class after navigation
          setTimeout(() => {
            if (surveyRoot) {
              surveyRoot.classList.remove("transitioning");
            }
          }, 50);
        }, 50);
      } else {
        sender.completeLastPage();
      }
    };

    // Handler to add inputmode="numeric" for number inputs
    const afterRenderQuestionInputHandler = (sender: any, options: any) => {
      const { htmlElement } = options;

      // Check if the htmlElement is an input with type="number"
      if (htmlElement && htmlElement.type === "number") {
        // Set inputmode to numeric for better mobile keyboard
        htmlElement.setAttribute("inputmode", "numeric");
      }
    };

    // Disable default auto-advance and use custom logic
    console.log("adding customAutoAdvanceHandler");
    if (!didAddCustomAutoAdvanceHandler) {
      didAddCustomAutoAdvanceHandler = true;
      survey.onValueChanged.add(customAutoAdvanceHandler);
    }

    // Clear dependent answers when experience changes
    const experienceChangeHandler = (sender: any, options: any) => {
      if (!options || options.name !== "experience") return;
      const experienceValue = sender.data?.experience;
      if (experienceValue === "Beginner") {
        const invalidNumberWorkouts = ["As many as possible"];
        if (invalidNumberWorkouts.includes(sender.data?.numberWorkouts)) {
          sender.setValue("numberWorkouts", undefined);
        }
        if (sender.data?.timeAvailable === "30OrLess") {
          sender.setValue("timeAvailable", undefined);
        }
      }
    };

    survey.onValueChanged.add(experienceChangeHandler);
    survey.onValueChanged.add(saveProgress);
    survey.onCurrentPageChanged.add(saveProgress);
    survey.sendResultOnPageNext = true;
    survey.onTextMarkdown.add(markdownToHtmlConverter);
    survey.onAfterRenderPage.add(onAfterRenderPageHandler);
    survey.onAfterRenderQuestionInput.add(afterRenderQuestionInputHandler);
    survey.onCurrentPageChanged.add(onCurrentPageChangedHandler);
    survey.onCompleting.add(onCompletingHandler);
    survey.onComplete.add(onCompleteHandler);
    survey.onCurrentPageChanging.add(onCurrentPageChangingHandler);
    survey.onAfterRenderPage.add(customGuestPassSkipNavigation);
    survey.onAfterRenderPage.add(project60OptOut);

    return () => {
      survey.onValueChanged.remove(customAutoAdvanceHandler);
      survey.onAfterRenderPage.remove(customGuestPassSkipNavigation);
      survey.onAfterRenderPage.remove(project60OptOut);
      survey.onValueChanged.remove(experienceChangeHandler);
      survey.onValueChanged.remove(saveProgress);
      survey.onCurrentPageChanged.remove(saveProgress);
      survey.onTextMarkdown.remove(markdownToHtmlConverter);
      survey.onAfterRenderPage.remove(onAfterRenderPageHandler);
      survey.onAfterRenderQuestionInput.remove(afterRenderQuestionInputHandler);
      survey.onCurrentPageChanged.remove(onCurrentPageChangedHandler);
      survey.onCompleting.remove(onCompletingHandler);
      survey.onComplete.remove(onCompleteHandler);
      survey.onCurrentPageChanging.remove(onCurrentPageChangingHandler);
      didAddCustomAutoAdvanceHandler = false;
    };
  }, [
    markdownToHtmlConverter,
    onAfterRenderPageHandler,
    onCurrentPageChangedHandler,
    onCompletingHandler,
    onCompleteHandler,
    survey,
  ]);

  useEffect(() => {
    const storedSurvey = localStorage.getItem(LOCAL_STORAGE_KEY);
    const { currentPageIndex = 0 } = storedSurvey
      ? JSON.parse(storedSurvey)
      : {};

    if (currentPageIndex && storedSurvey) {
      const { data } = JSON.parse(storedSurvey);
      setUser((prevUser) => ({ ...prevUser, ...data }));
    }
  }, []);

  useEffect(() => {
    const storedSurvey = localStorage.getItem(LOCAL_STORAGE_KEY);
    const { data = {} } = storedSurvey ? JSON.parse(storedSurvey) : {};
    const isSurveyComplete = !!data.focus;

    if (
      (window.location.pathname === "/results" ||
        window.location.pathname === "/checkout") &&
      isSurveyComplete
    ) {
      return;
    }
    if (window.location.pathname === "/bridge") {
      return;
    }
    navigate(`/page/${survey.currentPageNo + 1}`);
  }, [survey.currentPageNo, survey.currentPage.name, navigate]);

  return [
    survey,
    progressPercent,
    transitionComponent,
    transitionComponentName,
    setTransitionComponentKey,
    section,
    user,
  ];
}

function App() {
  var converter = new showdown.Converter();
  useImagePreloader();
  // const questions = SURVEY_JSON.pages.map(page => ({ name: page.name, title: page.elements[0].title }));
  // // take that survey json and convert it to a csv

  // return <>
  //   <div>
  //     {questions.map(question => <div>{question.name + ', ' + question.title}</div>)}
  //   </div>
  // </>;

  const [assignedExperiment, setAssignedExperiment] = useState<
    { experiment: string; arm: string } | undefined
  >(undefined);
  const overrideStableID = useMemo(
    () => Cookies.get("bws_statsig_stable_id"),
    []
  );
  const { client, isLoading } = useClientAsyncInit(
    STATSIG_CLIENT_KEY,
    {
      customIDs: {
        stableID: overrideStableID,
      },
    },
    {
      environment: {
        tier: process.env.NODE_ENV,
      },
    }
  );

  const [
    survey,
    progressPercent,
    TransitionComponent,
    transitionComponentName,
    setTransitionComponentKey,
    progressTitle,
    user,
  ] = useSurvey(SURVEY_JSON, converter, client, isLoading);

  const navigate = useNavigate();
  const location = useLocation();
  const initialLoadRef = useRef(true);

  function isValidUUIDv4(uuid: unknown): boolean {
    if (typeof uuid !== "string") {
      return false;
    }

    // UUID v4 format:
    // xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx
    // where x is any hexadecimal digit and y is one of 8, 9, A, or B
    const uuidV4Regex =
      /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

    return uuidV4Regex.test(uuid);
  }

  useEffect(() => {
    // Only capture UTM parameters if they are present in the URL
    const captureUtmParams = () => {
      const urlParams = new URLSearchParams(location.search);
      const utmSource = urlParams.get("utm_source");
      const utmMedium = urlParams.get("utm_medium");
      const utmCampaign = urlParams.get("utm_campaign");
      const utmTerm = urlParams.get("utm_term");
      const utmContent = urlParams.get("utm_content");
      const fbp = urlParams.get("_fbp");
      let fbc = urlParams.get("_fbc")

      // Convert fbclid to fbc if it's not already present
      if (!fbc && urlParams.get("fbclid") && !getCookie("_fbc")) {
        const fbclid = urlParams.get("fbclid");
        if (fbclid) {
          const creationTime = Date.now();
          fbc = `fb.2.${creationTime}.${fbclid}`;
        }
      }

      if (isValidUUIDv4(urlParams.get("amplitude_device_id"))) {
        const amplitude_device_id = urlParams.get("amplitude_device_id");
        if (amplitude_device_id) {
          Cookies.set("bws_amplitude_device_id", amplitude_device_id, {
            expires: 30,
            sameSite: "lax",
            domain:
              process.env.NODE_ENV === "development"
                ? "localhost"
                : "builtwithscience.com",
          });
        }
      }

      const params = {
        utm_source: utmSource,
        utm_medium: utmMedium,
        utm_campaign: utmCampaign,
        utm_term: utmTerm,
        utm_content: utmContent,
        _fbp: fbp,
        _fbc: fbc,
      };

      if (Object.values(params).some((value) => value)) {
        // Save to local storage
        localStorage.setItem("utmParams", JSON.stringify(params));

        // Save to cookies (with 30 days expiration)
        Object.entries(params).forEach(([key, value]) => {
          if (value) {
            Cookies.set(key, value, {
              expires: 30,
              sameSite: "lax",
              domain:
                process.env.NODE_ENV === "development"
                  ? "localhost"
                  : "builtwithscience.com",
            });
          }
        });
      }

      // Capture utm_angle for bridge screen experiment
      const utmAngle = urlParams.get("utm_angle");
      if (utmAngle) {
        localStorage.setItem("utm_angle", utmAngle);
      }
    };

    captureUtmParams();
  }, [location.search]);

  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const iacConnectionId = urlParams.get("iacConnectionId");
    if (iacConnectionId) {
      localStorage.setItem("iacConnectionId", iacConnectionId);
    }
    const mode = urlParams.get("mode");
    if (mode) {
      localStorage.setItem("mode", mode);
    }
    // Capture hs_offer query param for Hubspot promotional offer tracking
    const hsOffer = urlParams.get("hs_offer");
    if (hsOffer) {
      localStorage.setItem("hs_offer", hsOffer);
    }
  }, [location.search]);

  useEffect(() => {
    axios
      .get(
        "https://core.builtwithscience.com/internal/public/control-flag/get/checkoutv2"
      )
      .then((response) => {
        localStorage.setItem(
          "checkoutv2",
          response.data.data ? "enabled" : "disabled"
        );
      })
      .catch(() => {
        localStorage.setItem("checkoutv2", "disabled");
      });
    axios
      .get(
        "https://core.builtwithscience.com/internal/public/control-flag/get/checkoutv2TestMode"
      )
      .then((response) => {
        localStorage.setItem(
          "checkoutv2TestMode",
          response.data.data ? "enabled" : "disabled"
        );
      })
      .catch(() => {
        localStorage.setItem("checkoutv2TestMode", "disabled");
      });
  }, []);

  const onComplete = useCallback(() => {
    if (transitionComponentName != null) {
      sendPageViewEvent(
        user.goals,
        transitionComponentName,
        "",
        [],
        findPageIndex(transitionComponentName) + 1
      );
    }
    setTransitionComponentKey(null);
  }, [setTransitionComponentKey, transitionComponentName, user.goals]);

  useEffect(() => {
    if (
      window.location.pathname === "/results" ||
      window.location.pathname === "/checkout"
    ) {
      return;
    }
    if (initialLoadRef.current) {
      const storedSurvey = localStorage.getItem(LOCAL_STORAGE_KEY);

      const { currentPageIndex = 0 } = storedSurvey
        ? JSON.parse(storedSurvey)
        : {};

      // Returning users: navigate to saved page immediately
      if (currentPageIndex) {
        initialLoadRef.current = false;
        navigate(`/page/${currentPageIndex + 1}`);
        return;
      }

      // Check if bridge screen might apply (valid utm_angle with content)
      const utmAngle = localStorage.getItem("utm_angle");
      if (!utmAngle || !BRIDGE_CONTENT[utmAngle]) {
        // No bridge possible, navigate to Q1 immediately
        initialLoadRef.current = false;
        navigate(`/page/1`);
        return;
      }

      // Valid utm_angle present: wait for Statsig to check experiment
      if (isLoading) return;

      initialLoadRef.current = false;
      const arm = getAssignedExperimentArm("web23_bridge_screen", client);
      const showBridge = arm === "variation";
      if (showBridge) {
        navigate("/bridge");
      } else {
        navigate(`/page/1`);
      }
    }
  }, [navigate, isLoading]);

  interface UTMParams {
    utm_source: string;
    utm_medium: string;
    utm_campaign: string;
    utm_term: string | null;
    utm_content: string;
  }

  /* Get From Localstorage


  // Safe extraction with error handling
  const getUTMParams = (): UTMParams | null => {
    try {
      const storedData = localStorage.getItem('utmParams') ?? '';
      return JSON.parse(storedData) as UTMParams;
    } catch (error) {

      return null;
    }
  };

  */

  const hasFiredEvent = useRef(false); // <-- State guard

  useEffect(() => {
    if (!isLoading && !hasFiredEvent.current) {
      const overridebws_amplitude_device_ID = Cookies.get(
        "bws_amplitude_device_id"
      );
      // Fire your one-time event here

      const utm_Params = getUTMParams();
      if (overrideStableID === undefined) {
        Cookies.set("bws_statsig_stable_id", client.getContext().stableID, {
          expires: 365,
          sameSite: "lax",
          domain:
            process.env.NODE_ENV === "development"
              ? "localhost"
              : "builtwithscience.com",
        });
      }

      const quizEventData: QuizEventData = {
        event: "quiz",
        funnel_variation: FunnelVariation.General,
        quizz_name: "New Onboarding Quiz",
        quizz_step: "0",
        quizz_step_name: "Start",
        quizz_version: getVersionNumber(),
      };
      pushToDataLayer(quizEventData);
      client.logEvent("quiz", "0", {
        funnel_variation: FunnelVariation.General,
        step_name: "Start",
      });

      let amplitudeDeviceId = uuidv4();
      if (overridebws_amplitude_device_ID === undefined) {
        Cookies.set("bws_amplitude_device_id", amplitudeDeviceId, {
          expires: 365,
          sameSite: "lax",
          domain:
            process.env.NODE_ENV === "development"
              ? "localhost"
              : "builtwithscience.com",
        });
      } else {
        amplitudeDeviceId = overridebws_amplitude_device_ID;
      }

      amplitude.init(AMPLITUDE_API_KEY, undefined, {
        deviceId: amplitudeDeviceId,
        serverUrl: "https://analytics.builtwithscience.com/measure",
        autocapture: false,
      });

      if (utm_Params !== null) {
        const identifyObj = new Identify();
        if (
          utm_Params.utm_source !== null &&
          utm_Params.utm_source !== undefined
        )
          identifyObj.set("utm_source", utm_Params.utm_source);
        if (
          utm_Params.utm_medium !== null &&
          utm_Params.utm_medium !== undefined
        )
          identifyObj.set("utm_medium", utm_Params.utm_medium);
        if (
          utm_Params.utm_campaign !== null &&
          utm_Params.utm_campaign !== undefined
        )
          identifyObj.set("utm_campaign", utm_Params.utm_campaign);
        if (
          utm_Params.utm_content !== null &&
          utm_Params.utm_content !== undefined
        )
          identifyObj.set("utm_content", utm_Params.utm_content);
        if (utm_Params.utm_term !== null && utm_Params.utm_term !== undefined)
          identifyObj.set("utm_term", utm_Params.utm_term);
        amplitude.identify(identifyObj);
      }

      // Set utm_angle as user property and track experiment exposure
      const utmAngle = localStorage.getItem("utm_angle");
      if (utmAngle) {
        const angleIdentify = new Identify();
        angleIdentify.set("utm_angle", utmAngle);
        amplitude.identify(angleIdentify);

        if (BRIDGE_CONTENT[utmAngle]) {
          const arm = getAssignedExperimentArm("web23_bridge_screen", client);
          amplitude.track("ExperimentStarted", {
            experimentName: "web23_bridge_screen",
            variantName: arm === "variation" ? "treatment" : "control",
            utm_angle: utmAngle,
          });
        }
      }

      amplitude.track("quiz", {
        step: "0",
        step_name: "Start",
        funnel_variation: FunnelVariation.General,
        utm_source: utm_Params?.utm_source,
        utm_medium: utm_Params?.utm_medium,
        utm_campaign: utm_Params?.utm_campaign,
        utm_content: utm_Params?.utm_content,
        utm_term: utm_Params?.utm_term,
      });

      // Fire tracking events for quiz start
      trackCustom("QuizStarted");
      trackQuizStarted();

      console.log("amplitudeDeviceId", amplitudeDeviceId);
      hasFiredEvent.current = true; // Block future executions
    }
  }, [isLoading]); // Dependency on isLoading

  if (isLoading) {
    return <div></div>;
  }

  const currentPageProps = survey.currentPage.props ?? {};
  const showHeader = currentPageProps.showHeader ?? true;
  const showBackButton = currentPageProps.showBackButton ?? true;
  const showCurrentSection = currentPageProps.showCurrentSection ?? true;
  const nextButtonText = currentPageProps.nextButtonText ?? "Continue";
  const nextButtonCaption = currentPageProps.nextButtonCaption;
  const CustomHeader = currentPageProps.customHeader ?? null;
  const currentPageHeading = getQuestionHeading(survey.currentPage.name);
  const disclaimer = currentPageProps.disclaimer ?? null;
  const showCaptionSeparator = currentPageProps.showCaptionSeparator ?? true;

  const hsOffer = localStorage.getItem("hs_offer");

  return (
    <StatsigProvider client={client}>
      <AppContainer>
        <DevTool survey={survey} />
        <Routes>
          <Route
            path="/page/:pageNumber"
            element={
              <>
              {hsOffer === 'potato' ? (
                <Project60StickyBanner
                  line1="SPECIAL BONUS"
                  line2="30-Day CPB Diets Included Free With Your Personalized Plan"
                  showTimer={false}
                />
              ) : null}
                {progressTitle && TransitionComponent !== LandingPage ? (
                  <>
                    <BlackFridayTimer mini />
                  </>
                ) : null}
                {TransitionComponent == null ? (
                  <>
                    {CustomHeader && <CustomHeader />}
                    {showHeader && (
                      <HeaderContainer>
                        <ProgressHeader
                          title={progressTitle}
                          progress={progressPercent}
                        />
                      </HeaderContainer>
                    )}
                    <QuestionHeader
                      user={user}
                      survey={survey}
                      showBackButton={showBackButton}
                      showCurrentSection={showCurrentSection}
                      sectionName={currentPageHeading}
                    />
                    <Survey className="kokolo" model={survey} />
                    <QuestionFooter
                      survey={survey}
                      nextButtonText={nextButtonText}
                      nextButtonCaption={nextButtonCaption}
                      disclaimer={disclaimer}
                      showCaptionSeparator={showCaptionSeparator}
                    />
                  </>
                ) : (
                  <TransitionComponent
                    survey={survey}
                    user={user}
                    onComplete={onComplete}
                  />
                )}
              </>
            }
          />
          <Route
            path="/bridge"
            element={
              <BridgeScreen
                onContinue={() => navigate("/page/1")}
              />
            }
          />
         <Route
  path="/results"
  element={
    <>
      {hsOffer === 'potato' ? (
        <Project60StickyBanner
          line1="SPECIAL BONUS"
          line2="30-Day CPB Diets Included Free With Your Personalized Plan"
          showTimer={false}
        />
      ) : null}
      <LandingPage user={user} onComplete={onComplete} survey={survey} client={client} isClientLoading={isLoading} />
    </>
  }
/>
          <Route
            path="/checkout"
            element={
              <>
                {hsOffer === 'potato' ? (
                  <Project60StickyBanner
                    line1="SPECIAL BONUS"
                    line2="30-Day CPB Diets Included Free With Your Personalized Plan"
                    showTimer={false}
                  />
                ) : null}
                <CheckoutPage user={user} onComplete={onComplete} survey={survey} /> 
              </>
            }
          />
        </Routes>
      </AppContainer>
    </StatsigProvider>
  );
}

export default App;
